import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  BookOpen, GraduationCap, ChevronRight, ChevronLeft, CheckCircle2, Clock,
  ShieldCheck, FileText, Award, LogOut, AlertCircle, Loader2, Download,
  Building2, Briefcase, FileSignature, ArrowLeft, RefreshCw, Edit3, ExternalLink,
  Film, X as XIcon, Eye,
} from 'lucide-react';
import './onboarding.css';
import { toEmbedUrl, detectMediaKind } from '../../utils/embedUrl';
import WelcomePanel from './WelcomePanel';
import AnnualTrainingTab from './AnnualTrainingTab';

// =====================================================================
// TNSwork Staff Onboarding Page
// Routes:
//   • New hire (requires_staff_onboarding=true) → full-screen wizard
//   • Admin → tab with approval queue + staff overview
// =====================================================================
export default function OnboardingPage() {
  const { user, api, logout } = useAuth();
  const isAdmin = user?.role === 'administrator';
  const isReviewer = ['administrator', 'supervisor', 'hr'].includes(user?.role);

  // Reviewers see a tab UI; new hires see only their wizard
  // Default tab depends on role — HR/supervisor land on queue (no welcome edit access)
  const [adminTab, setAdminTab] = useState(isAdmin ? 'welcome' : 'queue');

  if (!user) return null;

  // If user is a new hire that needs onboarding, ALWAYS render wizard (full-screen)
  const isLockedIn = user.requires_staff_onboarding && !user.staff_onboarding_complete;

  if (isLockedIn) {
    return (
      <div className="onboarding-wizard p-6 md:p-10">
        <div className="max-w-5xl mx-auto">
          <Header user={user} onLogout={logout} locked />
          <NewHireWizard api={api} user={user} />
        </div>
      </div>
    );
  }

  // Otherwise: reviewer view (admin/supervisor/HR) or completed-staff "view my certificate" view
  return (
    <div className="onboarding-wizard p-6 md:p-10">
      <div className="max-w-6xl mx-auto">
        <Header user={user} onLogout={logout} locked={false} />
        {isReviewer ? (
          <>
            <AdminTabs tab={adminTab} setTab={setAdminTab} isAdmin={isAdmin} />
            {adminTab === 'welcome' && isAdmin && <WelcomePanel api={api} user={user} isAdmin={true} />}
            {adminTab === 'queue' && <ApprovalQueue api={api} />}
            {adminTab === 'staff' && <StaffOverview api={api} />}
            {adminTab === 'assignments' && <AssignmentsTab api={api} />}
            {adminTab === 'annual' && <AnnualTrainingTab api={api} />}
            {adminTab === 'mytraining' && <NewHireWizard api={api} user={user} />}
            {adminTab === 'modules' && isAdmin && <ModuleCatalog api={api} />}
          </>
        ) : (
          <NewHireWizard api={api} user={user} />
        )}
      </div>
    </div>
  );
}

// ---------- Header ----------
function Header({ user, onLogout, locked }) {
  return (
    <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
      <div>
        <div className="small-caps text-[var(--accent)] mb-1 flex items-center gap-1.5">
          <GraduationCap className="h-3.5 w-3.5" /> TNSwork Staff Orientation
        </div>
        <h1 className="display text-4xl md:text-5xl" data-testid="onboarding-title">
          {locked ? `Welcome, ${user.name?.split(' ')[0] || 'team'}.` : 'Orientation & Training'}
        </h1>
        {locked && (
          <p className="text-[var(--ink-soft)] mt-2 max-w-2xl">
            Complete your 24-hour orientation to unlock the rest of TNSwork. Self-attest each module, then your supervisor will sign off.
          </p>
        )}
      </div>
      {locked && (
        <button onClick={onLogout} className="btn-ghost text-sm flex items-center gap-1.5" data-testid="onboarding-logout">
          <LogOut className="h-4 w-4" /> Save & exit
        </button>
      )}
    </div>
  );
}

// =====================================================================
// New Hire Wizard
// =====================================================================
function NewHireWizard({ api, user }) {
  const [data, setData] = useState(null);
  const [activeModule, setActiveModule] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/onboarding/my-progress');
      setData(data);
    } catch (e) {
      toast.error('Failed to load progress');
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const downloadCert = async () => {
    try {
      const resp = await api.get(`/onboarding/certificate/${user.id}`, { responseType: 'blob' });
      const blob = new Blob([resp.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(user.name || 'staff').replace(/[^A-Za-z0-9]+/g, '_')}_certificate.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No certificate yet');
    }
  };

  if (!data) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[var(--accent)]" /></div>;
  }

  if (activeModule) {
    return (
      <ModulePlayer
        api={api}
        module={activeModule}
        onClose={() => { setActiveModule(null); load(); }}
      />
    );
  }

  const s = data.summary;
  const odp = data.modules.filter(m => m.category === 'odp');
  const sys = data.modules.filter(m => m.category === 'system');
  const isComplete = !!data.staff_onboarding_complete;

  return (
    <>
      {/* Welcome dashboard (admin-customizable) */}
      <WelcomePanel api={api} user={user} isAdmin={false} />

      {/* Orientation completion banner */}
      {isComplete && (
        <div
          className="bg-gradient-to-r from-[#F3EFE0] to-[#FBF6E8] border border-[#DAC58A] rounded-xl p-5 mb-6 flex items-center justify-between flex-wrap gap-3"
          data-testid="orientation-complete-banner"
        >
          <div className="flex items-center gap-3">
            <Award className="h-9 w-9 text-[var(--gold)] shrink-0" />
            <div>
              <div className="font-bold text-[var(--ink)]">Orientation complete</div>
              <div className="text-sm text-[var(--ink-soft)]">
                Any additional trainings assigned to you appear in the catalog below.
              </div>
            </div>
          </div>
          <button
            onClick={downloadCert}
            className="btn-primary flex items-center gap-1.5"
            data-testid="download-cert"
          >
            <Download className="h-4 w-4" /> Download Certificate
          </button>
        </div>
      )}

      {/* Big progress card */}
      <div className="bg-white border border-[var(--rule)] rounded-xl p-6 mb-6 shadow-sm" data-testid="progress-card">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
          <div>
            <div className="small-caps text-[var(--ink-fade)] mb-1">Your progress</div>
            <div className="display text-3xl">
              {s.approved_hours.toFixed(1)}<span className="text-[var(--ink-fade)] text-xl"> / {s.total_hours.toFixed(0)} hours</span>
            </div>
          </div>
          <div className="text-right">
            <div className="small-caps text-[var(--ink-fade)] mb-1">Status</div>
            <div className="text-lg font-bold">
              {s.progress_pct.toFixed(0)}% complete
              {s.submitted_count > 0 && <span className="ml-2 text-sm text-[var(--gold)] font-medium"> · {s.submitted_count} awaiting review</span>}
            </div>
          </div>
        </div>
        <div className="progress-bar"><div style={{ width: `${s.progress_pct}%` }} /></div>
      </div>

      {/* ODP section */}
      <Section
        title="ODP-Required Trainings"
        sub="12 hours of Office of Developmental Programs compliance"
        icon={<ShieldCheck className="h-5 w-5 text-[var(--accent)]" />}
        modules={odp}
        onOpen={setActiveModule}
      />

      {/* Systems section */}
      <Section
        title="Systems Training"
        sub="12 hours · 4 hours per system"
        icon={<FileText className="h-5 w-5 text-[var(--accent)]" />}
        modules={sys}
        onOpen={setActiveModule}
      />

      {/* Annual trainings — only shows when something is assigned */}
      {(data.annual_modules || []).length > 0 && (
        <Section
          title="Annual Training"
          sub="Assigned trainings with due dates — separate from your orientation"
          icon={<Award className="h-5 w-5 text-[var(--gold)]" />}
          modules={data.annual_modules}
          onOpen={setActiveModule}
        />
      )}
    </>
  );
}

function Section({ title, sub, icon, modules, onOpen }) {
  return (
    <div className="mb-8" data-testid={`section-${title.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <div>
          <h2 className="display text-xl">{title}</h2>
          <p className="text-xs text-[var(--ink-fade)]">{sub}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {modules.map(m => <ModuleCard key={m.id} module={m} onClick={() => onOpen(m)} />)}
      </div>
    </div>
  );
}

function ModuleCard({ module, onClick }) {
  const prog = module.progress;
  const status = prog?.status || 'not_started';
  const cls = status === 'approved' ? 'complete' : status === 'submitted' ? 'submitted' : status === 'rejected' ? 'rejected' : '';
  const chipCls = status === 'approved' ? 'chip-approved' :
                  status === 'submitted' ? 'chip-submitted' :
                  status === 'rejected' ? 'chip-rejected' :
                  status === 'in_progress' ? 'chip-progress' : 'chip-todo';
  const chipLabel = status === 'approved' ? 'Approved' :
                    status === 'submitted' ? 'Awaiting Review' :
                    status === 'rejected' ? 'Needs Redo' :
                    status === 'in_progress' ? 'In Progress' : 'Not Started';

  const SysIcon = module.system === 'tnswork' ? Building2
                : module.system === 'adp' ? Briefcase
                : module.system === 'therap' ? FileSignature
                : null;

  return (
    <button onClick={onClick} className={`module-card p-4 text-left ${cls}`} data-testid={`module-card-${module.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`chip ${module.category === 'odp' ? 'chip-odp' : module.category === 'annual' ? 'chip-annual' : 'chip-system'}`}>
              {module.category === 'odp' ? 'ODP' : module.category === 'annual' ? 'ANNUAL' : (module.system || 'system').toUpperCase()}
            </span>
            <span className={`chip ${chipCls}`} data-testid={`module-status-${module.id}`}>{chipLabel}</span>
          </div>
          <h3 className="font-bold text-[var(--ink)] flex items-center gap-1.5">
            {SysIcon && <SysIcon className="h-4 w-4 opacity-60" />}
            {module.title}
          </h3>
        </div>
        <div className="text-right shrink-0">
          <div className="flex items-center gap-1 text-[var(--ink-soft)] text-sm">
            <Clock className="h-3.5 w-3.5" /> {module.hours}h
          </div>
        </div>
      </div>
      <p className="text-xs text-[var(--ink-soft)] line-clamp-2">{module.description}</p>
      {module.due_date && status !== 'approved' && (
        <div className={`mt-2 text-xs font-semibold flex items-center gap-1 ${module.overdue ? 'text-red-600' : 'text-orange-700'}`} data-testid={`module-due-${module.id}`}>
          <Clock className="h-3.5 w-3.5" />
          {module.overdue
            ? `Overdue — was due ${module.due_date}`
            : `Due ${module.due_date}${module.days_until_due != null ? ` (${module.days_until_due}d)` : ''}`}
        </div>
      )}
      {status === 'approved' && (
        <div className="mt-2 flex items-center gap-1 text-xs text-[var(--leaf)] font-semibold">
          <CheckCircle2 className="h-3.5 w-3.5" /> Signed off by {prog.reviewed_by_name || 'supervisor'}
        </div>
      )}
      {status === 'rejected' && prog?.review_notes && (
        <div className="mt-2 text-xs text-[var(--danger)] italic">Feedback: {prog.review_notes}</div>
      )}
    </button>
  );
}

// =====================================================================
// Module Player — content + quiz + self-attest
// =====================================================================
function ModulePlayer({ api, module, onClose }) {
  const [step, setStep] = useState('content'); // content | quiz | attest
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setSubmitting(true);
    try {
      const quizAnswers = (module.quiz || []).map((_, i) => answers[i] ?? -1);
      const { data } = await api.post(`/onboarding/modules/${module.id}/submit`, {
        quiz_answers: quizAnswers,
        self_attested: true,
        notes: '',
      });
      setResult(data);
      if (data.passed) {
        toast.success('Submitted — awaiting supervisor approval');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    }
    setSubmitting(false);
  };

  const status = module.progress?.status || 'not_started';

  return (
    <div data-testid="module-player">
      <button onClick={onClose} className="btn-ghost mb-4 flex items-center gap-1.5" data-testid="back-to-list">
        <ArrowLeft className="h-4 w-4" /> Back to all modules
      </button>

      <div className="bg-white border border-[var(--rule)] rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <span className={`chip ${module.category === 'odp' ? 'chip-odp' : module.category === 'annual' ? 'chip-annual' : 'chip-system'}`}>
            {module.category === 'odp' ? 'ODP' : module.category === 'annual' ? 'ANNUAL' : (module.system || 'system').toUpperCase()}
          </span>
          <span className="text-sm text-[var(--ink-fade)] flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {module.hours}h</span>
        </div>
        <h2 className="display text-3xl mb-2">{module.title}</h2>
        <p className="text-[var(--ink-soft)] mb-6">{module.description}</p>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6">
          {['content', 'quiz', 'attest'].map((s, i) => (
            <React.Fragment key={s}>
              <div className={`small-caps px-3 py-1.5 rounded-full ${step === s ? 'bg-[var(--accent)] text-white' : 'bg-[var(--rule-soft)] text-[var(--ink-fade)]'}`}>
                {i + 1}. {s === 'content' ? 'Material' : s === 'quiz' ? 'Knowledge Check' : 'Self-Attest'}
              </div>
              {i < 2 && <ChevronRight className="h-3 w-3 text-[var(--ink-fade)]" />}
            </React.Fragment>
          ))}
        </div>

        {step === 'content' && (
          <ContentStep module={module} onNext={() => setStep('quiz')} />
        )}
        {step === 'quiz' && (
          <QuizStep
            module={module}
            answers={answers}
            setAnswers={setAnswers}
            onBack={() => setStep('content')}
            onNext={() => setStep('attest')}
          />
        )}
        {step === 'attest' && (
          <AttestStep
            module={module}
            status={status}
            result={result}
            submitting={submitting}
            onBack={() => setStep('quiz')}
            onSubmit={submit}
            onClose={onClose}
            onRetry={() => { setResult(null); setAnswers({}); setStep('quiz'); }}
          />
        )}
      </div>
    </div>
  );
}

function ContentStep({ module, onNext }) {
  const lessons = module.lessons || [];

  // If module has lessons, render them as a sequential list
  if (lessons.length > 0) {
    return <LessonList module={module} lessons={lessons} onNext={onNext} />;
  }

  // Otherwise render the single-content view (legacy / simple modules)
  const embedUrl = toEmbedUrl(module.content_url);
  const isPdf = /\.pdf(\?|$)/i.test(module.content_url || '');
  const isVideoFile = /\.(mp4|webm|ogg)(\?|$)/i.test(module.content_url || '');
  const kind = detectMediaKind(module.content_url);

  return (
    <div>
      {embedUrl && (
        <div className="mb-4 rounded-lg overflow-hidden border border-[var(--rule)] bg-black" data-testid="embed-frame">
          {isVideoFile ? (
            <video controls src={embedUrl} className="w-full h-full" style={{ aspectRatio: '16/9' }} />
          ) : (
            <iframe src={embedUrl} title={module.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen className="w-full"
              style={{ aspectRatio: isPdf ? '4/5' : '16/9', border: 0 }} />
          )}
        </div>
      )}
      {module.content_url && !embedUrl && (
        <div className="mb-4 bg-[var(--rule-soft)] border border-[var(--rule)] rounded-lg p-4 flex items-center gap-2 text-sm">
          <ExternalLink className="h-4 w-4 text-[var(--accent)] shrink-0" />
          <a href={module.content_url} target="_blank" rel="noreferrer" className="text-[var(--accent)] underline truncate">Open training material ({kind})</a>
        </div>
      )}
      {module.content_text && (
        <div className="bg-[var(--rule-soft)] rounded-lg p-5 mb-4" data-testid="content-body">
          <p className="whitespace-pre-wrap leading-relaxed text-[var(--ink)]">{module.content_text}</p>
        </div>
      )}
      {!embedUrl && !module.content_url && !module.content_text && (
        <div className="bg-[var(--rule-soft)] rounded-lg p-5 mb-4 italic text-[var(--ink-fade)] text-sm" data-testid="content-empty">
          No training material attached yet. Your supervisor will share material before you continue.
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--ink-fade)] italic">
          {embedUrl ? 'Watch the full video, then proceed to the knowledge check.' : 'Read through the material above, then proceed to the knowledge check.'}
        </p>
        <button className="btn-primary flex items-center gap-1" onClick={onNext} data-testid="to-quiz">
          Continue to quiz <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function LessonList({ module, lessons, onNext }) {
  const [watched, setWatched] = useState({});
  const [activeIdx, setActiveIdx] = useState(0);
  const allDone = lessons.every((_, i) => watched[i]);

  const toggle = (i) => {
    setWatched(prev => ({ ...prev, [i]: !prev[i] }));
  };

  return (
    <div data-testid="lesson-list">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-[var(--ink-soft)]">
          This module is broken into <strong>{lessons.length} lessons</strong>. Watch each one and mark it complete.
        </p>
        <span className="text-xs text-[var(--ink-fade)]">
          {Object.values(watched).filter(Boolean).length} / {lessons.length} watched
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Lesson list */}
        <div className="space-y-1.5 max-h-[460px] overflow-y-auto pr-1">
          {lessons.map((le, i) => {
            const done = !!watched[i];
            return (
              <button
                key={le.id || i}
                onClick={() => setActiveIdx(i)}
                className={`w-full text-left p-2.5 rounded-lg border transition flex items-start gap-2 ${activeIdx === i ? 'border-[var(--accent)] bg-[#F4EFFA]' : done ? 'border-[var(--leaf)] bg-[#F1F8F1]' : 'border-[var(--rule)] hover:border-[var(--accent)]'}`}
                data-testid={`lesson-item-${i}`}>
                <span className="text-xs font-bold text-[var(--ink-fade)] shrink-0 mt-0.5">{i + 1}.</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[var(--ink)] truncate">{le.title}</div>
                  <div className="text-[10px] text-[var(--ink-fade)] flex items-center gap-1 mt-0.5">
                    <Clock className="h-2.5 w-2.5" /> {le.duration_min} min
                    {le.content_url && <span className="text-[var(--accent)] font-semibold">· video</span>}
                  </div>
                </div>
                {done && <CheckCircle2 className="h-4 w-4 text-[var(--leaf)] shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Active lesson player */}
        <div>
          <ActiveLesson lesson={lessons[activeIdx]} idx={activeIdx} total={lessons.length} done={!!watched[activeIdx]} onMark={() => toggle(activeIdx)} onNext={() => setActiveIdx(Math.min(activeIdx + 1, lessons.length - 1))} />
        </div>
      </div>

      <div className="flex items-center justify-between mt-5 pt-4 border-t border-[var(--rule)]">
        <p className="text-xs text-[var(--ink-fade)] italic">
          {allDone ? '✓ All lessons watched — proceed to the knowledge check.' : `Watch the remaining ${lessons.length - Object.values(watched).filter(Boolean).length} lesson(s) to unlock the quiz.`}
        </p>
        <button onClick={onNext} disabled={!allDone} className="btn-primary flex items-center gap-1" data-testid="to-quiz">
          Continue to quiz <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function ActiveLesson({ lesson, idx, total, done, onMark, onNext }) {
  if (!lesson) return null;
  const embedUrl = toEmbedUrl(lesson.content_url);
  const isPdf = /\.pdf(\?|$)/i.test(lesson.content_url || '');
  const isVideoFile = /\.(mp4|webm|ogg)(\?|$)/i.test(lesson.content_url || '');
  const kind = detectMediaKind(lesson.content_url);

  return (
    <div data-testid={`active-lesson-${idx}`}>
      <div className="mb-2">
        <div className="small-caps text-[var(--ink-fade)] mb-1">Lesson {idx + 1} of {total}</div>
        <h3 className="display text-lg">{lesson.title}</h3>
        <div className="text-xs text-[var(--ink-fade)] flex items-center gap-1 mt-0.5">
          <Clock className="h-3 w-3" /> {lesson.duration_min} min
        </div>
      </div>

      {embedUrl && (
        <div className="mt-3 rounded-lg overflow-hidden border border-[var(--rule)] bg-black">
          {isVideoFile ? (
            <video controls src={embedUrl} className="w-full" style={{ aspectRatio: '16/9' }} />
          ) : (
            <iframe src={embedUrl} title={lesson.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen className="w-full"
              style={{ aspectRatio: isPdf ? '4/5' : '16/9', border: 0 }} />
          )}
        </div>
      )}

      {lesson.content_url && !embedUrl && (
        <div className="mt-3 bg-[var(--rule-soft)] border border-[var(--rule)] rounded-lg p-3 flex items-center gap-2 text-sm">
          <ExternalLink className="h-4 w-4 text-[var(--accent)] shrink-0" />
          <a href={lesson.content_url} target="_blank" rel="noreferrer" className="text-[var(--accent)] underline truncate">Open material ({kind})</a>
        </div>
      )}

      {lesson.content_text && (
        <div className="mt-3 bg-[var(--rule-soft)] rounded-lg p-4 text-sm">
          <p className="whitespace-pre-wrap leading-relaxed text-[var(--ink)]">{lesson.content_text}</p>
        </div>
      )}

      {!embedUrl && !lesson.content_url && !lesson.content_text && (
        <div className="mt-3 bg-[var(--rule-soft)] rounded-lg p-4 text-sm italic text-[var(--ink-fade)]">
          No material attached for this lesson yet.
        </div>
      )}

      <div className="flex items-center justify-between mt-4 gap-2 flex-wrap">
        <button onClick={onMark} className={done ? 'btn-ghost' : 'btn-primary'} data-testid={`mark-lesson-${idx}`}>
          {done ? '✓ Marked complete (click to undo)' : 'Mark complete'}
        </button>
        {idx < total - 1 && (
          <button onClick={onNext} className="btn-ghost flex items-center gap-1">Next lesson <ChevronRight className="h-4 w-4" /></button>
        )}
      </div>
    </div>
  );
}

function QuizStep({ module, answers, setAnswers, onBack, onNext }) {
  const quiz = module.quiz || [];
  const allAnswered = quiz.every((_, i) => answers[i] != null);

  return (
    <div>
      <div className="space-y-5" data-testid="quiz-questions">
        {quiz.map((q, i) => (
          <div key={i} className="bg-[var(--rule-soft)] rounded-lg p-4">
            <h4 className="font-bold text-[var(--ink)] mb-3 flex items-start gap-2">
              <span className="text-[var(--accent)] shrink-0">{i + 1}.</span>
              <span>{q.question}</span>
            </h4>
            <div className="space-y-2">
              {q.choices.map((c, ci) => (
                <button
                  key={ci}
                  onClick={() => setAnswers(prev => ({ ...prev, [i]: ci }))}
                  className={`quiz-option ${answers[i] === ci ? 'selected' : ''}`}
                  data-testid={`quiz-${i}-choice-${ci}`}>
                  <span className="quiz-radio" />
                  <span>{c}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-6">
        <button onClick={onBack} className="btn-ghost flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> Back to material</button>
        <button disabled={!allAnswered} onClick={onNext} className="btn-primary flex items-center gap-1" data-testid="to-attest">
          Review & attest <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function AttestStep({ module, status, result, submitting, onBack, onSubmit, onClose, onRetry }) {
  if (result?.passed === false) {
    return (
      <div className="text-center py-8" data-testid="quiz-failed">
        <AlertCircle className="h-12 w-12 mx-auto text-[var(--danger)] mb-3" />
        <h3 className="display text-2xl mb-2">Almost there.</h3>
        <p className="text-[var(--ink-soft)] mb-1">
          You scored <strong>{result.score}%</strong> — you need <strong>{result.threshold}%</strong> to pass.
        </p>
        <p className="text-[var(--ink-fade)] text-sm mb-6 italic">Review the material and try the quiz again — no limit on retries.</p>
        <button onClick={onRetry} className="btn-primary flex items-center gap-1 mx-auto" data-testid="quiz-retry">
          <RefreshCw className="h-4 w-4" /> Try the quiz again
        </button>
      </div>
    );
  }

  if (result?.passed) {
    return (
      <div className="text-center py-8" data-testid="attest-success">
        <CheckCircle2 className="h-14 w-14 mx-auto text-[var(--leaf)] mb-3" />
        <h3 className="display text-2xl mb-2">Submitted!</h3>
        <p className="text-[var(--ink-soft)] mb-1">
          You scored <strong>{result.score}%</strong>. Your supervisor will review and sign off shortly.
        </p>
        <p className="text-[var(--ink-fade)] text-sm mb-6 italic">You'll see it move to "Approved" on your dashboard once they confirm.</p>
        <button onClick={onClose} className="btn-primary" data-testid="back-to-dashboard">
          Back to onboarding
        </button>
      </div>
    );
  }

  if (status === 'submitted') {
    return (
      <div className="text-center py-8">
        <Clock className="h-12 w-12 mx-auto text-[var(--gold)] mb-3" />
        <h3 className="display text-2xl mb-2">Awaiting review</h3>
        <p className="text-[var(--ink-soft)] mb-6 italic">Your supervisor will sign off on this module shortly.</p>
        <button onClick={onClose} className="btn-ghost">Back to onboarding</button>
      </div>
    );
  }

  return (
    <div data-testid="attest-step">
      <div className="bg-[var(--rule-soft)] rounded-lg p-5 mb-5">
        <h4 className="font-bold mb-3 flex items-center gap-1.5"><FileSignature className="h-4 w-4 text-[var(--accent)]" /> Self-Attestation</h4>
        <p className="text-sm text-[var(--ink-soft)] leading-relaxed">
          By submitting, you confirm that you have read the training material for <strong>{module.title}</strong>,
          completed the knowledge check honestly, and understand the responsibilities described.
          Your supervisor will review and sign off before this module counts toward your {module.hours}-hour requirement.
        </p>
      </div>
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="btn-ghost flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> Back to quiz</button>
        <button onClick={onSubmit} disabled={submitting} className="btn-primary flex items-center gap-1.5" data-testid="submit-attest">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />}
          {submitting ? 'Submitting…' : 'I attest — submit'}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Admin views
// =====================================================================
function AdminTabs({ tab, setTab, isAdmin }) {
  const tabs = [
    isAdmin && { k: 'welcome', l: 'Welcome page' },
    { k: 'queue', l: 'Approval queue' },
    { k: 'staff', l: 'Staff overview' },
    { k: 'assignments', l: 'Assignments' },
    { k: 'annual', l: 'Annual Training' },
    { k: 'mytraining', l: 'My Training' },
    isAdmin && { k: 'modules', l: 'Module catalog' },
  ].filter(Boolean);
  return (
    <div className="flex items-center gap-1 mb-6 border-b border-[var(--rule)]" data-testid="admin-tabs">
      {tabs.map(t => (
        <button
          key={t.k}
          onClick={() => setTab(t.k)}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition ${tab === t.k ? 'text-[var(--accent)] border-[var(--accent)]' : 'text-[var(--ink-fade)] border-transparent hover:text-[var(--accent)]'}`}
          data-testid={`tab-${t.k}`}>
          {t.l}
        </button>
      ))}
    </div>
  );
}

function ApprovalQueue({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/onboarding/pending-approvals');
      setItems(data);
    } catch (e) { /* soft */ }
    setLoading(false);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const review = async (item, approve) => {
    const key = `${item.employee_id}_${item.module_id}`;
    setActing(prev => ({ ...prev, [key]: true }));
    let notes = '';
    if (!approve) {
      notes = window.prompt('Reason for rejection (optional, shown to new hire):') || '';
    }
    try {
      await api.post(`/onboarding/progress/${item.employee_id}/${item.module_id}/review`, {
        approve, notes,
      });
      toast.success(approve ? 'Approved' : 'Sent back for redo');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Review failed');
    }
    setActing(prev => ({ ...prev, [key]: false }));
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[var(--accent)]" /></div>;
  if (items.length === 0) {
    return (
      <div className="bg-white border border-[var(--rule)] rounded-xl p-10 text-center" data-testid="empty-queue">
        <CheckCircle2 className="h-10 w-10 mx-auto text-[var(--leaf)] mb-3" />
        <h3 className="display text-xl mb-1">Nothing pending</h3>
        <p className="text-[var(--ink-fade)] text-sm">All submitted attestations have been reviewed.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="approval-queue">
      {items.map(item => {
        const key = `${item.employee_id}_${item.module_id}`;
        return (
          <div key={key} className="bg-white border border-[var(--rule)] rounded-lg p-4 flex items-center gap-3 flex-wrap" data-testid={`approval-${key}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 mb-1">
                <span className={`chip ${item.module_category === 'odp' ? 'chip-odp' : item.module_category === 'annual' ? 'chip-annual' : 'chip-system'}`}>
                  {item.module_category === 'odp' ? 'ODP' : item.module_category === 'annual' ? 'ANNUAL' : 'SYSTEM'}
                </span>
                <span className="text-xs text-[var(--ink-fade)]"><Clock className="h-3 w-3 inline" /> {item.module_hours}h</span>
                <span className="text-xs font-bold text-[var(--leaf)]">Quiz {item.quiz_score}%</span>
              </div>
              <div className="font-bold text-[var(--ink)]">{item.module_title}</div>
              <div className="text-sm text-[var(--ink-soft)]">{item.employee_name} · {item.employee_email}</div>
              <div className="text-xs text-[var(--ink-fade)] italic mt-1">
                Submitted {item.self_attested_at?.slice(0, 16).replace('T', ' at ')}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => review(item, false)}
                disabled={acting[key]}
                className="btn-ghost text-sm text-[var(--danger)] border-[var(--danger)]/30 hover:border-[var(--danger)]"
                data-testid={`reject-${key}`}>
                Reject
              </button>
              <button
                onClick={() => review(item, true)}
                disabled={acting[key]}
                className="btn-primary text-sm flex items-center gap-1"
                data-testid={`approve-${key}`}>
                {acting[key] ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Approve
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StaffOverview({ api }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');                       // all | in_progress | complete | pending_review
  const [transcriptFor, setTranscriptFor] = useState(null);          // {id, name} → opens dialog

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get('/onboarding/staff'),
        api.get('/employees'),
      ]);
      setStaff(s.data);
      setAllEmployees(e.data || []);
    } catch (err) { /* soft */ }
    setLoading(false);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const enroll = async (employeeId, enrolled) => {
    setEnrolling(true);
    try {
      await api.post('/onboarding/enroll', { employee_id: employeeId, enrolled });
      toast.success(enrolled ? 'Enrolled in onboarding' : 'Removed from onboarding');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setEnrolling(false);
  };

  const enrolledIds = new Set(staff.map(s => s.id));
  const candidates = allEmployees.filter(e => !enrolledIds.has(e.id) && !e.archived && e.name?.toLowerCase().includes(search.toLowerCase()));

  const downloadCert = async (id, name) => {
    try {
      const resp = await api.get(`/onboarding/certificate/${id}`, { responseType: 'blob' });
      const blob = new Blob([resp.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(name || 'staff').replace(/[^A-Za-z0-9]+/g, '_')}_certificate.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download certificate');
    }
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get('/onboarding/staff/export.csv', { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `onboarding_progress_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error('Could not export');
    }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[var(--accent)]" /></div>;

  const filteredStaff = staff.filter(s => {
    if (filter === 'in_progress') return !s.staff_onboarding_complete && s.pct < 100;
    if (filter === 'complete') return s.staff_onboarding_complete;
    if (filter === 'pending_review') return (s.submitted_count || 0) > 0;
    return true;
  });

  return (
    <div data-testid="staff-overview">
      {/* Enrollment picker */}
      <div className="bg-white border border-[var(--rule)] rounded-lg p-4 mb-4" data-testid="enroll-section">
        <h3 className="display text-base mb-2 flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-[var(--accent)]" /> Enroll a new hire</h3>
        <input
          type="text"
          placeholder="Search staff to enroll…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full px-3 py-2 border border-[var(--rule)] rounded text-sm mb-2"
          data-testid="enroll-search"
        />
        {search && candidates.length > 0 && (
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {candidates.slice(0, 8).map(e => (
              <button
                key={e.id}
                onClick={() => { enroll(e.id, true); setSearch(''); }}
                disabled={enrolling}
                className="w-full text-left px-3 py-2 hover:bg-[var(--rule-soft)] rounded text-sm flex items-center justify-between"
                data-testid={`enroll-candidate-${e.id}`}>
                <span><strong>{e.name}</strong> <span className="text-[var(--ink-fade)]">· {e.email || e.role}</span></span>
                <span className="text-[var(--accent)] text-xs font-semibold">Enroll →</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filter + export */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-1" data-testid="staff-filters">
          {[
            { k: 'all', l: `All (${staff.length})` },
            { k: 'in_progress', l: `In progress (${staff.filter(s => !s.staff_onboarding_complete && s.pct < 100).length})` },
            { k: 'pending_review', l: `Awaiting review (${staff.filter(s => (s.submitted_count || 0) > 0).length})` },
            { k: 'complete', l: `Complete (${staff.filter(s => s.staff_onboarding_complete).length})` },
          ].map(f => (
            <button key={f.k} onClick={() => setFilter(f.k)}
              className={`text-xs px-3 py-1.5 rounded-md border transition ${filter === f.k ? 'bg-[var(--accent)] text-white border-[var(--accent)]' : 'bg-white text-[var(--ink-soft)] border-[var(--rule)] hover:border-[var(--accent)]'}`}
              data-testid={`filter-${f.k}`}>
              {f.l}
            </button>
          ))}
        </div>
        <button onClick={exportCsv} className="btn-ghost text-xs flex items-center gap-1.5 border border-[var(--rule)] hover:border-[var(--accent)]" data-testid="export-csv-btn">
          <Download className="h-3 w-3" /> Export CSV
        </button>
      </div>

      {/* Staff list */}
      <div className="space-y-2" data-testid="staff-rows">
        {filteredStaff.length === 0 && (
          <div className="text-center text-[var(--ink-fade)] py-8 italic text-sm">
            {staff.length === 0 ? 'No staff currently enrolled. Use the search above to enroll a new hire.' : 'No staff match the current filter.'}
          </div>
        )}
        {filteredStaff.map(s => (
          <div key={s.id} className="bg-white border border-[var(--rule)] rounded-lg p-4 flex items-center gap-3 flex-wrap hover:border-[var(--accent)] transition-colors" data-testid={`staff-${s.id}`}>
            <button onClick={() => setTranscriptFor({ id: s.id, name: s.name })} className="flex-1 min-w-[200px] text-left" data-testid={`staff-name-${s.id}`} title="Click to view full transcript">
              <div className="font-bold text-[var(--ink)] hover:text-[var(--accent)] flex items-center gap-1.5">
                {s.name}
                <ChevronRight className="h-3 w-3 opacity-60" />
              </div>
              <div className="text-xs text-[var(--ink-fade)]">{s.email} · {s.role}</div>
            </button>
            <div className="flex-1 min-w-[180px]">
              <div className="progress-bar"><div style={{ width: `${s.pct}%` }} /></div>
              <div className="text-xs text-[var(--ink-soft)] mt-1">
                {s.approved_hours} / {s.total_hours}h · {s.pct.toFixed(0)}%
                {s.submitted_count > 0 && <span className="text-[var(--gold)] font-medium"> · {s.submitted_count} awaiting review</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {s.staff_onboarding_complete ? (
                <>
                  <span className="chip chip-approved flex items-center gap-1"><Award className="h-3 w-3" /> Complete</span>
                  <button onClick={() => downloadCert(s.id, s.name)} className="btn-ghost text-xs flex items-center gap-1" data-testid={`cert-${s.id}`}>
                    <Download className="h-3 w-3" /> Cert
                  </button>
                </>
              ) : (
                <button onClick={() => enroll(s.id, false)} disabled={enrolling} className="btn-ghost text-xs">Remove</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {transcriptFor && (
        <TranscriptDialog api={api} employeeId={transcriptFor.id} employeeName={transcriptFor.name} onClose={() => setTranscriptFor(null)} />
      )}
    </div>
  );
}

function TranscriptDialog({ api, employeeId, employeeName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/onboarding/staff/${employeeId}/transcript`)
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) toast.error('Failed to load transcript'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, employeeId]);

  const printTranscript = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow || !data) return;
    const rows = (data.transcript || []).map(t => `
      <tr>
        <td>${t.module_title}</td>
        <td>${t.module_category}${t.module_system ? ' · ' + t.module_system : ''}</td>
        <td>${t.module_hours}h</td>
        <td><strong>${t.status.replace('_', ' ')}</strong></td>
        <td>${t.quiz_score != null ? t.quiz_score + '%' : '—'}</td>
        <td>${t.attempts || 0}</td>
        <td>${t.reviewed_by_name || '—'}</td>
        <td>${(t.reviewed_at || t.self_attested_at || '').slice(0, 10)}</td>
      </tr>`).join('');
    printWindow.document.write(`<!doctype html><html><head><title>${data.employee.name} — Onboarding Transcript</title>
      <style>body{font-family:Inter,system-ui,sans-serif;color:#2D1B4E;padding:24px;max-width:900px;margin:auto}
      h1{font-size:24px;margin-bottom:4px}h2{font-size:14px;color:#5A4D6E;font-weight:normal;margin-top:0}
      table{width:100%;border-collapse:collapse;margin-top:16px;font-size:12px}
      th,td{text-align:left;padding:6px 8px;border-bottom:1px solid #E0D8E8}
      th{background:#F0EDF6;text-transform:uppercase;font-size:10px;letter-spacing:0.04em}
      .summary{margin-top:8px;padding:12px;background:#F8F5FB;border-radius:6px;font-size:12px}
      </style></head><body>
      <h1>${data.employee.name}</h1>
      <h2>${data.employee.email} · ${data.employee.role} · Onboarding Transcript</h2>
      <div class="summary">
        <strong>Progress:</strong> ${data.summary.approved_hours} / ${data.summary.total_hours} hours
        (${data.summary.pct}%) ·
        ${data.employee.complete ? '<strong style="color:#1B5E20">Complete</strong>' : 'In progress'}
        ${data.summary.submitted_count > 0 ? ' · ' + data.summary.submitted_count + ' awaiting review' : ''}
      </div>
      <table><thead><tr>
        <th>Module</th><th>Category</th><th>Hours</th><th>Status</th><th>Score</th><th>Attempts</th><th>Reviewed By</th><th>Date</th>
      </tr></thead><tbody>${rows}</tbody></table>
      </body></html>`);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 400);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose} data-testid="transcript-dialog">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[92vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-[var(--rule)] flex items-center justify-between">
          <div>
            <div className="small-caps text-[var(--ink-fade)]">Onboarding transcript</div>
            <h3 className="display text-xl">{employeeName}</h3>
            {data && (
              <div className="text-xs text-[var(--ink-fade)] mt-0.5">
                {data.employee.email} · {data.employee.role}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={printTranscript} disabled={!data} className="btn-ghost text-sm flex items-center gap-1 disabled:opacity-50" data-testid="print-transcript">
              <FileText className="h-3.5 w-3.5" /> Print / Save PDF
            </button>
            <button onClick={onClose} className="btn-ghost !p-2" data-testid="close-transcript"><XIcon className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading && <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[var(--accent)]" /></div>}
          {data && (
            <>
              {/* Summary card */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                <Stat label="Approved" value={`${data.summary.approved_hours}h`} />
                <Stat label="Target" value={`${data.summary.total_hours}h`} />
                <Stat label="Progress" value={`${data.summary.pct}%`} />
                <Stat label="Awaiting" value={data.summary.submitted_count} />
                <Stat label="Status" value={data.employee.complete ? 'Complete' : 'In progress'} />
              </div>
              <div className="progress-bar mb-5"><div style={{ width: `${data.summary.pct}%` }} /></div>

              {/* Transcript table */}
              <table className="w-full text-sm" data-testid="transcript-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--ink-fade)] border-b border-[var(--rule)]">
                    <th className="py-2 pr-2">Module</th>
                    <th className="py-2 px-2">Hours</th>
                    <th className="py-2 px-2">Status</th>
                    <th className="py-2 px-2">Score</th>
                    <th className="py-2 px-2">Tries</th>
                    <th className="py-2 px-2">Completed</th>
                    <th className="py-2 pl-2">Reviewer</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.transcript || []).map(t => {
                    const statusColor = {
                      approved: 'text-[var(--leaf)]',
                      submitted: 'text-[var(--gold)]',
                      rejected: 'text-[var(--danger)]',
                      not_started: 'text-[var(--ink-fade)]',
                    }[t.status] || 'text-[var(--ink-soft)]';
                    return (
                      <tr key={t.module_id} className={`border-b border-[var(--rule-soft)] ${!t.module_is_active ? 'opacity-50' : ''}`} data-testid={`transcript-row-${t.module_id}`}>
                        <td className="py-2 pr-2">
                          <div className="font-medium text-[var(--ink)]">{t.module_title}</div>
                          <div className="text-[10px] text-[var(--ink-fade)] uppercase tracking-wider">{t.module_category}{t.module_system && ' · ' + t.module_system}{!t.module_is_active && ' · inactive'}</div>
                          {t.review_notes && <div className="text-[11px] text-[var(--danger)] italic mt-0.5">Note: {t.review_notes}</div>}
                        </td>
                        <td className="py-2 px-2 text-[var(--ink-soft)] tabular-nums">{t.module_hours}</td>
                        <td className={`py-2 px-2 font-semibold capitalize ${statusColor}`}>{t.status.replace('_', ' ')}</td>
                        <td className="py-2 px-2 tabular-nums text-[var(--ink-soft)]">{t.quiz_score != null ? `${t.quiz_score}%` : '—'}</td>
                        <td className="py-2 px-2 tabular-nums text-[var(--ink-soft)]">{t.attempts || 0}</td>
                        <td className="py-2 px-2 tabular-nums text-[var(--ink-soft)]" data-testid={`transcript-completed-${t.module_id}`}>
                          {t.completed_at ? t.completed_at.slice(0, 10) : (t.self_attested_at ? <span className="italic text-[var(--ink-fade)]" title="Submitted, awaiting approval">{t.self_attested_at.slice(0, 10)}</span> : '—')}
                        </td>
                        <td className="py-2 pl-2 text-[var(--ink-soft)]">
                          {t.reviewed_by_name || '—'}
                          {t.reviewed_at && <div className="text-[10px] text-[var(--ink-fade)]">{t.reviewed_at.slice(0, 10)}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(data.isp_trainings || []).length > 0 && (
                <div className="mt-4" data-testid="transcript-isp-section">
                  <div className="small-caps text-[var(--ink-fade)] mb-2">ISP Trainings (recorded outside the system)</div>
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[10px] uppercase tracking-wider text-[var(--ink-fade)] bg-[#F8F5FB]">
                      <th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5">Hours</th><th className="px-2 py-1.5">Participant</th><th className="px-2 py-1.5">Trainer</th><th className="px-2 py-1.5">Notes</th>
                    </tr></thead>
                    <tbody>
                      {data.isp_trainings.map(t => (
                        <tr key={t.id} className="border-t border-[var(--rule)]">
                          <td className="px-2 py-1.5 tabular-nums">{t.date}</td>
                          <td className="px-2 py-1.5 tabular-nums">{t.hours}</td>
                          <td className="px-2 py-1.5">{t.participant}</td>
                          <td className="px-2 py-1.5">{t.trainer_name}</td>
                          <td className="px-2 py-1.5 text-[var(--ink-soft)]">{t.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Full Module Catalog — admins create, edit, delete, and toggle modules.
function ModuleCatalog({ api }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // module object or "new"
  const [previewing, setPreviewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/onboarding/modules?all=1');
      setModules(data);
    } catch (e) { /* soft */ }
    setLoading(false);
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (m) => {
    try {
      await api.put(`/onboarding/modules/${m.id}`, { is_active: !m.is_active });
      load();
    } catch (e) { toast.error('Toggle failed'); }
  };

  const removeModule = async (m) => {
    if (!window.confirm(`Delete the module "${m.title}"? This cannot be undone (existing staff progress on it will be lost).`)) return;
    try {
      await api.delete(`/onboarding/modules/${m.id}`);
      toast.success('Module deleted');
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[var(--accent)]" /></div>;

  const odpHrs = modules.filter(m => m.category === 'odp' && m.is_active).reduce((a, b) => a + b.hours, 0);
  const sysHrs = modules.filter(m => m.category === 'system' && m.is_active).reduce((a, b) => a + b.hours, 0);

  return (
    <div data-testid="module-catalog">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
          <Stat label="Modules" value={modules.length} />
          <Stat label="Active" value={modules.filter(m => m.is_active).length} />
          <Stat label="ODP hours" value={odpHrs} />
          <Stat label="System hours" value={sysHrs} />
        </div>
        <button onClick={() => setEditing('new')} className="btn-primary flex items-center gap-1.5" data-testid="add-module">
          <Edit3 className="h-4 w-4" /> + New module
        </button>
      </div>

      <div className="space-y-2">
        {modules.map(m => {
          const kind = m.content_url ? detectMediaKind(m.content_url) : null;
          const lessonCount = (m.lessons || []).length;
          const lessonsWithMedia = (m.lessons || []).filter(le => le.content_url).length;
          return (
            <div key={m.id} className="bg-white border border-[var(--rule)] rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid={`catalog-${m.id}`}>
              <span className={`chip ${m.category === 'odp' ? 'chip-odp' : m.category === 'annual' ? 'chip-annual' : 'chip-system'}`}>
                {m.category === 'odp' ? 'ODP' : m.category === 'annual' ? 'ANNUAL' : (m.system || 'system').toUpperCase()}
              </span>
              <div className="flex-1 min-w-[180px]">
                <div className="font-semibold flex items-center gap-2 flex-wrap">
                  {m.title}
                  {kind && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[var(--accent)] text-white rounded flex items-center gap-1">
                      <Film className="h-2.5 w-2.5" /> {kind}
                    </span>
                  )}
                  {lessonCount > 0 && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[#E8F0FE] text-[#1A3677] rounded">
                      {lessonCount} lessons {lessonsWithMedia > 0 && `· ${lessonsWithMedia} w/ video`}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--ink-fade)]">{m.hours}h · {(m.quiz || []).length} questions · {m.pass_threshold || 80}% pass</div>
              </div>
              <button onClick={() => setPreviewing(m)} className="btn-ghost text-xs flex items-center gap-1" data-testid={`preview-row-${m.id}`}>
                <Eye className="h-3 w-3" /> Preview
              </button>
              <button onClick={() => setEditing(m)} className="btn-ghost text-xs flex items-center gap-1" data-testid={`edit-${m.id}`}>
                <Edit3 className="h-3 w-3" /> Edit
              </button>
              <button onClick={() => toggle(m)} className={`chip ${m.is_active ? 'chip-approved' : 'chip-todo'} cursor-pointer`} data-testid={`toggle-${m.id}`}>
                {m.is_active ? 'Active' : 'Inactive'}
              </button>
              <button onClick={() => removeModule(m)} className="btn-ghost !p-2 text-[var(--danger)] hover:border-[var(--danger)]" title="Delete module" data-testid={`delete-${m.id}`}>
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-[var(--ink-fade)] italic mt-4">
        Tip: click <strong>Edit</strong> to break a module into 30-minute lessons, attach Loom/YouTube/PDF links per lesson, write quiz questions, and adjust required hours.
      </p>

      {editing && (
        <ModuleEditDialog
          module={editing === 'new' ? null : editing}
          api={api}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {previewing && (
        <ModulePreview module={previewing} onClose={() => setPreviewing(null)} />
      )}
    </div>
  );
}

function blankModule() {
  return {
    title: '',
    category: 'system',
    system: '',
    hours: 1.0,
    order: 99,
    description: '',
    content_url: '',
    content_text: '',
    lessons: [],
    quiz: [],
    pass_threshold: 80,
    is_active: true,
  };
}

function ModuleEditDialog({ module, api, onClose, onSaved }) {
  const isNew = !module;
  const initial = useMemo(() => module ? { ...blankModule(), ...module } : blankModule(), [module]);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('details'); // details | lessons | quiz
  const [previewing, setPreviewing] = useState(false);
  // Track whether the user actually changed anything. Compared structurally
  // against the snapshot we opened with so we don't nag on no-op closes.
  const isDirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(initial), [draft, initial]);
  // Track whether a mousedown started on the backdrop. Prevents accidental
  // closes when the user is selecting text inside an input and releases the
  // mouse outside the panel (the wonky behaviour the user reported).
  const backdropMouseDownRef = React.useRef(false);

  const requestClose = useCallback(() => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  }, [isDirty, onClose]);

  const onBackdropMouseDown = (e) => {
    backdropMouseDownRef.current = e.target === e.currentTarget;
  };
  const onBackdropMouseUp = (e) => {
    const startedOnBackdrop = backdropMouseDownRef.current;
    backdropMouseDownRef.current = false;
    if (startedOnBackdrop && e.target === e.currentTarget) {
      requestClose();
    }
  };

  // Esc closes (with the same dirty-guard)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose]);

  // Auto-compute hours from lessons sum (only when lessons exist)
  const hoursFromLessons = useMemo(() => {
    const sum = (draft.lessons || []).reduce((a, le) => a + (parseFloat(le.duration_min) || 0), 0);
    return sum / 60;
  }, [draft.lessons]);

  const save = async () => {
    if (!draft.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: draft.title,
        category: draft.category,
        system: draft.system || null,
        hours: (draft.lessons || []).length > 0 ? hoursFromLessons : parseFloat(draft.hours) || 0,
        order: parseInt(draft.order) || 99,
        description: draft.description || '',
        content_type: draft.content_type || 'text',
        content_url: draft.content_url || '',
        content_text: draft.content_text || '',
        lessons: (draft.lessons || []).map(le => ({
          id: le.id || undefined,
          title: le.title || 'Untitled lesson',
          duration_min: parseFloat(le.duration_min) || 30,
          content_type: le.content_type || 'video',
          content_url: le.content_url || '',
          content_text: le.content_text || '',
        })),
        quiz: (draft.quiz || []).filter(q => q.question?.trim()).map(q => ({
          question: q.question.trim(),
          choices: q.choices.filter(c => c?.trim()),
          correct_idx: parseInt(q.correct_idx) || 0,
        })),
        pass_threshold: parseFloat(draft.pass_threshold) || 80,
        is_active: draft.is_active !== false,
      };
      if (isNew) {
        await api.post('/onboarding/modules', payload);
        toast.success('Module created');
      } else {
        await api.put(`/onboarding/modules/${module.id}`, payload);
        toast.success('Module saved');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onMouseDown={onBackdropMouseDown}
      onMouseUp={onBackdropMouseUp}
      data-testid="module-edit-dialog">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[92vh] flex flex-col shadow-2xl" onMouseDown={e => e.stopPropagation()} onMouseUp={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-5 border-b border-[var(--rule)] flex items-center justify-between">
          <div>
            <div className="small-caps text-[var(--ink-fade)] flex items-center gap-2">
              {isNew ? 'New module' : 'Edit module'}
              {isDirty && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FFF4D6] text-[#8A6500] border border-[#E6C76A] font-bold uppercase tracking-wider" data-testid="dirty-badge">Unsaved</span>}
            </div>
            <h3 className="display text-xl">{draft.title || 'Untitled module'}</h3>
            <div className="text-xs text-[var(--ink-fade)] mt-0.5">
              {(draft.lessons || []).length > 0 ? (
                <>{(draft.lessons || []).length} lessons · {hoursFromLessons.toFixed(1)}h auto-computed</>
              ) : (
                <>{(parseFloat(draft.hours) || 0).toFixed(1)}h (set manually)</>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreviewing(true)}
              disabled={!draft.title}
              className="btn-ghost text-sm flex items-center gap-1 disabled:opacity-50"
              title="See exactly what a new hire would see"
              data-testid="preview-module">
              <Eye className="h-3.5 w-3.5" /> Preview
            </button>
            <button onClick={requestClose} className="btn-ghost !p-2"><XIcon className="h-4 w-4" /></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-5 border-b border-[var(--rule)] flex gap-1">
          {['details', 'lessons', 'quiz'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider border-b-2 transition ${tab === t ? 'text-[var(--accent)] border-[var(--accent)]' : 'text-[var(--ink-fade)] border-transparent hover:text-[var(--accent)]'}`}
              data-testid={`edit-tab-${t}`}>
              {t === 'details' ? 'Module details' : t === 'lessons' ? `Lessons (${(draft.lessons || []).length})` : `Quiz (${(draft.quiz || []).length})`}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'details' && <DetailsTab draft={draft} setDraft={setDraft} hoursAuto={hoursFromLessons} hasLessons={(draft.lessons || []).length > 0} />}
          {tab === 'lessons' && <LessonsTab draft={draft} setDraft={setDraft} />}
          {tab === 'quiz' && <QuizTab draft={draft} setDraft={setDraft} />}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-[var(--rule)] flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={draft.is_active !== false}
              onChange={e => setDraft({ ...draft, is_active: e.target.checked })}
              data-testid="edit-active"
            />
            <span className={draft.is_active !== false ? 'font-semibold text-[var(--leaf)]' : 'text-[var(--ink-fade)]'}>
              {draft.is_active !== false ? 'Active' : 'Inactive — hidden from new hires'}
            </span>
          </label>
          <div className="flex items-center gap-2">
            <button onClick={requestClose} className="btn-ghost">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-1.5" data-testid="save-module">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isNew ? 'Create module' : 'Save module'}
            </button>
          </div>
        </div>
      </div>

      {previewing && (
        <ModulePreview module={draft} onClose={() => setPreviewing(false)} />
      )}
    </div>
  );
}

// ----- Details tab -----
function DetailsTab({ draft, setDraft, hoursAuto, hasLessons }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label className="small-caps text-[var(--ink-fade)] block mb-1">Title</label>
        <input className="w-full px-3 py-2 border border-[var(--rule)] rounded text-base font-semibold"
          value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })}
          data-testid="edit-title" placeholder="e.g. CPR & First Aid Refresher" />
      </div>
      <div>
        <label className="small-caps text-[var(--ink-fade)] block mb-1">Category</label>
        <select className="w-full px-3 py-2 border border-[var(--rule)] rounded text-sm"
          value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })}
          data-testid="edit-category">
          <option value="odp">ODP-required</option>
          <option value="system">Systems training</option>
          <option value="annual">Annual training</option>
        </select>
      </div>
      <div>
        <label className="small-caps text-[var(--ink-fade)] block mb-1">System (if applicable)</label>
        <select className="w-full px-3 py-2 border border-[var(--rule)] rounded text-sm"
          value={draft.system || ''} onChange={e => setDraft({ ...draft, system: e.target.value })}>
          <option value="">— none —</option>
          <option value="tnswork">TNSwork</option>
          <option value="adp">ADP</option>
          <option value="therap">Therap</option>
        </select>
      </div>
      <div>
        <label className="small-caps text-[var(--ink-fade)] block mb-1">
          Hours {hasLessons && <span className="normal-case tracking-normal italic text-[var(--ink-fade)]">— auto from lessons</span>}
        </label>
        <input type="number" step="0.25" min="0"
          className="w-full px-3 py-2 border border-[var(--rule)] rounded text-sm disabled:bg-[var(--rule-soft)]"
          value={hasLessons ? hoursAuto.toFixed(2) : draft.hours}
          disabled={hasLessons}
          onChange={e => setDraft({ ...draft, hours: parseFloat(e.target.value) || 0 })}
          data-testid="edit-hours" />
      </div>
      <div>
        <label className="small-caps text-[var(--ink-fade)] block mb-1">Order (sort)</label>
        <input type="number" className="w-full px-3 py-2 border border-[var(--rule)] rounded text-sm"
          value={draft.order} onChange={e => setDraft({ ...draft, order: parseInt(e.target.value) || 99 })} />
      </div>
      <div>
        <label className="small-caps text-[var(--ink-fade)] block mb-1">Quiz pass threshold (%)</label>
        <input type="number" min="0" max="100" className="w-full px-3 py-2 border border-[var(--rule)] rounded text-sm"
          value={draft.pass_threshold} onChange={e => setDraft({ ...draft, pass_threshold: parseFloat(e.target.value) || 0 })} />
      </div>
      <div className="md:col-span-2">
        <label className="small-caps text-[var(--ink-fade)] block mb-1">Description</label>
        <textarea rows={3} className="w-full px-3 py-2 border border-[var(--rule)] rounded text-sm"
          value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })}
          placeholder="Short summary shown on the module card" />
      </div>
      {!hasLessons && (
        <div className="md:col-span-2 bg-[#FFF3CD] border border-[#FFE08A] rounded-lg p-3 text-sm text-[#856404]">
          💡 Tip: For multi-hour modules, switch to the <strong>Lessons</strong> tab and break it into 30-minute sections. Each lesson gets its own Loom / YouTube link.
        </div>
      )}
    </div>
  );
}

// ----- Lessons tab -----
function LessonsTab({ draft, setDraft }) {
  const lessons = draft.lessons || [];
  const update = (i, patch) => {
    const next = [...lessons];
    next[i] = { ...next[i], ...patch };
    setDraft({ ...draft, lessons: next });
  };
  const add = () => {
    setDraft({
      ...draft,
      lessons: [...lessons, { title: `Lesson ${lessons.length + 1}`, duration_min: 30, content_url: '', content_text: '', content_type: 'video' }],
    });
  };
  const remove = (i) => {
    const next = lessons.filter((_, idx) => idx !== i);
    setDraft({ ...draft, lessons: next });
  };
  const move = (i, dir) => {
    const next = [...lessons];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setDraft({ ...draft, lessons: next });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[var(--ink-soft)]">
          Break this module into bite-size lessons. Each gets its own Loom / video link.
          Total: <strong>{(lessons.reduce((a, b) => a + (parseFloat(b.duration_min) || 0), 0) / 60).toFixed(2)}h</strong>
        </p>
        <button onClick={add} className="btn-primary text-sm flex items-center gap-1" data-testid="add-lesson">+ Add lesson</button>
      </div>

      {lessons.length === 0 && (
        <div className="text-center py-8 text-[var(--ink-fade)] italic text-sm border border-dashed border-[var(--rule)] rounded-lg">
          No lessons yet. Click <strong>+ Add lesson</strong> to start breaking this module into sections.
        </div>
      )}

      <div className="space-y-3">
        {lessons.map((le, i) => {
          const kind = le.content_url ? detectMediaKind(le.content_url) : null;
          const embed = toEmbedUrl(le.content_url);
          return (
            <div key={le.id || i} className="border border-[var(--rule)] rounded-lg p-3 bg-[var(--rule-soft)]" data-testid={`lesson-row-${i}`}>
              <div className="flex items-start gap-2 mb-2">
                <div className="text-sm font-bold text-[var(--accent)] w-7 text-right pt-2">{i + 1}.</div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_120px] gap-2">
                  <input className="px-2.5 py-1.5 border border-[var(--rule)] rounded text-sm font-semibold bg-white"
                    placeholder="Lesson title (e.g. 'Login & Dashboard Walkthrough')"
                    value={le.title} onChange={e => update(i, { title: e.target.value })}
                    data-testid={`lesson-title-${i}`} />
                  <div className="relative">
                    <input type="number" min="5" step="5" className="w-full px-2.5 py-1.5 border border-[var(--rule)] rounded text-sm bg-white pr-10"
                      value={le.duration_min} onChange={e => update(i, { duration_min: parseFloat(e.target.value) || 0 })}
                      data-testid={`lesson-duration-${i}`} />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[var(--ink-fade)]">min</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1 pt-0.5">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="btn-ghost !p-1 disabled:opacity-30" title="Move up"><ChevronLeft className="h-3 w-3 rotate-90" /></button>
                  <button onClick={() => move(i, 1)} disabled={i === lessons.length - 1} className="btn-ghost !p-1 disabled:opacity-30" title="Move down"><ChevronRight className="h-3 w-3 rotate-90" /></button>
                </div>
                <button onClick={() => remove(i)} className="btn-ghost !p-2 text-[var(--danger)] hover:border-[var(--danger)]" title="Delete lesson" data-testid={`lesson-delete-${i}`}>
                  <XIcon className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="ml-9">
                <div className="relative flex items-center gap-1">
                  <input type="url"
                    placeholder="Loom / YouTube / Vimeo / PDF URL — OR use Upload →"
                    className="flex-1 px-2.5 py-1.5 border border-[var(--rule)] rounded text-xs font-mono bg-white"
                    value={le.content_url} onChange={e => update(i, { content_url: e.target.value })}
                    data-testid={`lesson-url-${i}`} />
                  <label className="px-2 py-1.5 border border-[var(--rule)] rounded text-xs bg-white hover:border-[var(--accent)] hover:text-[var(--accent)] cursor-pointer whitespace-nowrap"
                         data-testid={`lesson-upload-${i}`} title="Upload video (≤50MB), PDF, or image directly">
                    Upload
                    <input type="file" accept="video/*,application/pdf,image/*" className="hidden"
                           onChange={async (e) => {
                             const f = e.target.files?.[0]; if (!f) return;
                             if (f.size > 50 * 1024 * 1024) { toast.error('Max 50MB per file'); e.target.value=''; return; }
                             const dataUrl = await new Promise((res, rej) => {
                               const r = new FileReader();
                               r.onload = () => res(r.result); r.onerror = rej;
                               r.readAsDataURL(f);
                             });
                             update(i, { content_url: dataUrl });
                             toast.success(`${f.name} uploaded (${(f.size/1024/1024).toFixed(1)}MB)`);
                             e.target.value = '';
                           }} />
                  </label>
                  {kind && <span className="absolute right-16 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[var(--accent)] text-white rounded pointer-events-none">{kind}</span>}
                </div>
                <textarea rows={2} className="w-full px-2.5 py-1.5 border border-[var(--rule)] rounded text-xs bg-white mt-2"
                  placeholder="Lesson notes (optional)"
                  value={le.content_text} onChange={e => update(i, { content_text: e.target.value })} />
                {embed && (
                  <div className="mt-2 rounded-md overflow-hidden border border-[var(--rule)] bg-black" style={{ maxHeight: 220, aspectRatio: '16/9' }}>
                    <iframe src={embed} title={le.title} className="w-full h-full" style={{ border: 0 }} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ----- Quiz tab -----
function QuizTab({ draft, setDraft }) {
  const quiz = draft.quiz || [];
  const update = (i, patch) => {
    const next = [...quiz];
    next[i] = { ...next[i], ...patch };
    setDraft({ ...draft, quiz: next });
  };
  const updateChoice = (i, ci, val) => {
    const next = [...quiz];
    const choices = [...(next[i].choices || [])];
    choices[ci] = val;
    next[i] = { ...next[i], choices };
    setDraft({ ...draft, quiz: next });
  };
  const addChoice = (i) => {
    const next = [...quiz];
    next[i] = { ...next[i], choices: [...(next[i].choices || []), ''] };
    setDraft({ ...draft, quiz: next });
  };
  const removeChoice = (i, ci) => {
    const next = [...quiz];
    const choices = (next[i].choices || []).filter((_, idx) => idx !== ci);
    let correct = next[i].correct_idx || 0;
    if (correct >= choices.length) correct = 0;
    next[i] = { ...next[i], choices, correct_idx: correct };
    setDraft({ ...draft, quiz: next });
  };
  const moveQuestion = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= quiz.length) return;
    const next = [...quiz];
    [next[i], next[j]] = [next[j], next[i]];
    setDraft({ ...draft, quiz: next });
  };
  const duplicate = (i) => {
    const next = [...quiz];
    next.splice(i + 1, 0, JSON.parse(JSON.stringify(quiz[i])));
    setDraft({ ...draft, quiz: next });
  };
  const add = () => {
    setDraft({ ...draft, quiz: [...quiz, { question: '', choices: ['', '', '', ''], correct_idx: 0 }] });
  };
  const remove = (i) => {
    if (!window.confirm(`Delete question ${i + 1}? This can't be undone until you cancel out of the dialog.`)) return;
    setDraft({ ...draft, quiz: quiz.filter((_, idx) => idx !== i) });
  };

  return (
    <div>
      {/* Sticky header — counts + add */}
      <div className="sticky top-0 -mt-5 -mx-5 px-5 py-3 bg-white border-b border-[var(--rule)] flex items-center justify-between mb-4 z-10">
        <div>
          <p className="text-sm font-semibold text-[var(--ink)]">
            {quiz.length} {quiz.length === 1 ? 'question' : 'questions'}
            <span className="text-[var(--ink-fade)] font-normal ml-1">· need ≥ {draft.pass_threshold || 80}% correct to pass</span>
          </p>
          <p className="text-xs text-[var(--ink-fade)]">Pick the green radio next to the correct answer. Empty questions are silently dropped on save.</p>
        </div>
        <button onClick={add} className="btn-primary text-sm flex items-center gap-1 shrink-0" data-testid="add-question">
          + Add question
        </button>
      </div>

      {quiz.length === 0 && (
        <div className="text-center py-10 text-[var(--ink-fade)] text-sm border-2 border-dashed border-[var(--rule)] rounded-lg">
          <FileText className="h-7 w-7 mx-auto mb-2 opacity-50" />
          No quiz questions yet. Click <strong className="text-[var(--accent)]">+ Add question</strong> to start.
        </div>
      )}

      <div className="space-y-3">
        {quiz.map((q, i) => (
          <div key={i} className="border border-[var(--rule)] rounded-lg bg-white shadow-sm overflow-hidden" data-testid={`question-row-${i}`}>
            {/* Q header bar */}
            <div className="flex items-center gap-2 px-3 py-2 bg-[var(--rule-soft)] border-b border-[var(--rule)]">
              <span className="text-xs font-bold text-[var(--accent)] uppercase tracking-wider">Question {i + 1}</span>
              <div className="flex-1" />
              <button onClick={() => moveQuestion(i, -1)} disabled={i === 0} className="btn-ghost !p-1 disabled:opacity-30" title="Move up" data-testid={`q-up-${i}`}>
                <ChevronLeft className="h-3.5 w-3.5 rotate-90" />
              </button>
              <button onClick={() => moveQuestion(i, 1)} disabled={i === quiz.length - 1} className="btn-ghost !p-1 disabled:opacity-30" title="Move down" data-testid={`q-down-${i}`}>
                <ChevronRight className="h-3.5 w-3.5 rotate-90" />
              </button>
              <button onClick={() => duplicate(i)} className="btn-ghost !p-1" title="Duplicate" data-testid={`q-dup-${i}`}>
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => remove(i)} className="btn-ghost !p-1 text-[var(--danger)] hover:bg-red-50" title="Delete question" data-testid={`q-remove-${i}`}>
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Question text */}
            <div className="p-3">
              <textarea
                className="w-full px-3 py-2 border border-[var(--rule)] rounded text-sm bg-white font-medium leading-snug resize-none focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
                rows={2}
                placeholder="What do you want to ask?"
                value={q.question} onChange={e => update(i, { question: e.target.value })}
                data-testid={`q-text-${i}`} />

              {/* Choices */}
              <div className="mt-3 space-y-1.5">
                {(q.choices || []).map((c, ci) => {
                  const isCorrect = q.correct_idx === ci;
                  return (
                    <div
                      key={ci}
                      className={`flex items-center gap-2 p-1.5 rounded border transition ${isCorrect ? 'bg-[#E8F5E9] border-[var(--leaf)]' : 'bg-white border-[var(--rule)] hover:border-[var(--accent)]'}`}>
                      <label className="flex items-center gap-1.5 cursor-pointer pl-1.5 shrink-0" title="Mark as correct">
                        <input type="radio" name={`correct-${i}`} checked={isCorrect}
                          className="accent-[var(--leaf)] h-3.5 w-3.5"
                          onChange={() => update(i, { correct_idx: ci })}
                          data-testid={`q-correct-${i}-${ci}`} />
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${isCorrect ? 'text-[var(--leaf)]' : 'text-[var(--ink-fade)]'}`}>
                          {isCorrect ? '✓ Correct' : `${String.fromCharCode(65 + ci)}`}
                        </span>
                      </label>
                      <input
                        className="flex-1 px-2 py-1.5 border border-[var(--rule)] rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
                        placeholder={`Choice ${String.fromCharCode(65 + ci)}`}
                        value={c} onChange={e => updateChoice(i, ci, e.target.value)} />
                      <button
                        onClick={() => removeChoice(i, ci)}
                        disabled={(q.choices || []).length <= 2}
                        className="btn-ghost !p-1 text-[var(--danger)] hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                        title={(q.choices || []).length <= 2 ? 'Need at least 2 choices' : 'Remove choice'}>
                        <XIcon className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
                <button
                  onClick={() => addChoice(i)}
                  className="text-xs px-2.5 py-1 rounded border border-dashed border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-white transition flex items-center gap-1 w-fit"
                  data-testid={`q-add-choice-${i}`}>
                  + Add choice
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {quiz.length > 0 && (
        <button onClick={add} className="mt-4 w-full py-2.5 border-2 border-dashed border-[var(--accent)] text-[var(--accent)] rounded-lg text-sm font-semibold hover:bg-[var(--accent)] hover:text-white transition flex items-center justify-center gap-1" data-testid="add-question-bottom">
          + Add another question
        </button>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-[var(--rule)] rounded-lg p-3">
      <div className="small-caps text-[var(--ink-fade)]">{label}</div>
      <div className="display text-2xl">{value}</div>
    </div>
  );
}

// =====================================================================
// ModulePreview — admin sees the exact new-hire experience for a module,
// without affecting any real progress or submitting anything.
// =====================================================================
function ModulePreview({ module, onClose }) {
  const [step, setStep] = useState('content'); // content | quiz | attest
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(null);

  const quiz = module.quiz || [];
  const safeModule = useMemo(() => ({
    ...module,
    quiz: quiz.map(q => ({ question: q.question, choices: q.choices })),
  }), [module, quiz]);

  const tryGrade = () => {
    if (!quiz.length) {
      setShowResult({ passed: true, score: 100, threshold: module.pass_threshold || 80 });
      return;
    }
    let correct = 0;
    quiz.forEach((q, i) => { if (answers[i] === q.correct_idx) correct++; });
    const score = Math.round((correct / quiz.length) * 1000) / 10;
    const threshold = module.pass_threshold || 80;
    setShowResult({ passed: score >= threshold, score, threshold });
  };

  const totalHours = (safeModule.lessons || []).length > 0
    ? ((safeModule.lessons || []).reduce((a, b) => a + (parseFloat(b.duration_min) || 0), 0) / 60).toFixed(1)
    : safeModule.hours;

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] overflow-y-auto" onClick={onClose} data-testid="module-preview">
      <div className="min-h-full flex items-start justify-center p-4">
        <div className="bg-[var(--bg)] rounded-xl max-w-5xl w-full my-8 shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
          {/* Preview banner */}
          <div className="bg-gradient-to-r from-[#FFF3CD] to-[#FFE08A] border-b border-[#FFCA28] px-5 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-[#856404]">
              <Eye className="h-4 w-4" />
              <strong>Preview mode</strong>
              <span className="opacity-80">— exactly what a new hire would see. Nothing is saved.</span>
            </div>
            <button onClick={onClose} className="btn-ghost !p-1.5 !bg-white" data-testid="close-preview"><XIcon className="h-4 w-4" /></button>
          </div>

          <div className="p-5 md:p-8 onboarding-wizard">
            <div className="flex items-center gap-2 mb-3">
              <span className={`chip ${safeModule.category === 'odp' ? 'chip-odp' : safeModule.category === 'annual' ? 'chip-annual' : 'chip-system'}`}>
                {safeModule.category === 'odp' ? 'ODP' : safeModule.category === 'annual' ? 'ANNUAL' : (safeModule.system || 'system').toUpperCase()}
              </span>
              <span className="text-sm text-[var(--ink-fade)] flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" /> {totalHours}h
              </span>
            </div>
            <h2 className="display text-3xl mb-2 text-[var(--ink)]">{safeModule.title || 'Untitled'}</h2>
            <p className="text-[var(--ink-soft)] mb-6">{safeModule.description}</p>

            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {['content', 'quiz', 'attest'].map((s, i) => (
                <React.Fragment key={s}>
                  <button onClick={() => { setStep(s); setShowResult(null); }}
                    className={`small-caps px-3 py-1.5 rounded-full ${step === s ? 'bg-[var(--accent)] text-white' : 'bg-[var(--rule-soft)] text-[var(--ink-fade)] hover:text-[var(--accent)]'}`}>
                    {i + 1}. {s === 'content' ? 'Material' : s === 'quiz' ? 'Knowledge Check' : 'Self-Attest'}
                  </button>
                  {i < 2 && <ChevronRight className="h-3 w-3 text-[var(--ink-fade)]" />}
                </React.Fragment>
              ))}
            </div>

            {step === 'content' && (
              <ContentStep module={safeModule} onNext={() => setStep('quiz')} />
            )}
            {step === 'quiz' && quiz.length > 0 && !showResult && (
              <QuizStep module={safeModule} answers={answers} setAnswers={setAnswers}
                onBack={() => setStep('content')} onNext={tryGrade} />
            )}
            {step === 'quiz' && quiz.length === 0 && (
              <div className="bg-[var(--rule-soft)] rounded-lg p-6 text-center italic text-[var(--ink-fade)]">
                No quiz questions defined — the new hire would skip this step.
                <div className="mt-4">
                  <button onClick={() => setStep('attest')} className="btn-primary">Skip to attest</button>
                </div>
              </div>
            )}
            {step === 'quiz' && showResult && (
              <div className={`rounded-lg p-6 text-center ${showResult.passed ? 'bg-[#E8F5E9] border border-[var(--leaf)]' : 'bg-[#FDECEA] border border-[var(--danger)]'}`}>
                {showResult.passed ? <CheckCircle2 className="h-10 w-10 mx-auto text-[var(--leaf)] mb-2" /> : <AlertCircle className="h-10 w-10 mx-auto text-[var(--danger)] mb-2" />}
                <div className="display text-2xl mb-1">
                  {showResult.passed ? `Pass — ${showResult.score}%` : `Below threshold — ${showResult.score}%`}
                </div>
                <p className="text-sm text-[var(--ink-soft)] mb-4">
                  {showResult.passed
                    ? `A new hire would be sent to the supervisor for approval (needs ≥${showResult.threshold}%).`
                    : `A new hire would be asked to retake the quiz (needs ${showResult.threshold}%).`}
                </p>
                <div className="flex items-center justify-center gap-2">
                  <button onClick={() => { setShowResult(null); setAnswers({}); }} className="btn-ghost text-sm">Retake quiz</button>
                  {showResult.passed && (
                    <button onClick={() => setStep('attest')} className="btn-primary text-sm">See attest step →</button>
                  )}
                </div>
              </div>
            )}
            {step === 'attest' && (
              <div>
                <div className="bg-[var(--rule-soft)] rounded-lg p-5 mb-5">
                  <h4 className="font-bold mb-3 flex items-center gap-1.5"><FileSignature className="h-4 w-4 text-[var(--accent)]" /> Self-Attestation</h4>
                  <p className="text-sm text-[var(--ink-soft)] leading-relaxed">
                    By submitting, you confirm that you have read the training material for <strong>{safeModule.title}</strong>,
                    completed the knowledge check honestly, and understand the responsibilities described.
                    Your supervisor will review and sign off before this module counts toward your {totalHours}-hour requirement.
                  </p>
                </div>
                <div className="bg-[#E8F0FE] border border-[#A8BFE8] rounded-lg p-4 flex items-center gap-2 text-sm text-[#1A3677]">
                  <Eye className="h-4 w-4 shrink-0" />
                  <span>In preview mode, "I attest — submit" is disabled. Close the preview when you're done reviewing.</span>
                </div>
                <div className="flex items-center justify-between mt-5">
                  <button onClick={() => setStep('quiz')} className="btn-ghost flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> Back to quiz</button>
                  <button disabled className="btn-primary disabled:opacity-40 flex items-center gap-1.5">
                    <FileSignature className="h-4 w-4" /> I attest — submit (preview)
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



// ---------- Assignments tab — assign trainings with due dates throughout the year ----------
function AssignmentsTab({ api }) {
  const [assignments, setAssignments] = useState([]);
  const [modules, setModules] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);       // {employee_id, module_id, employee_name, module_title, due_date, assignment_note}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, m, s] = await Promise.all([
        api.get('/onboarding/assignments'),
        api.get('/onboarding/modules'),
        api.get('/employees'),
      ]);
      setAssignments(a.data);
      setModules(m.data);
      setStaff(s.data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const deleteAssignment = async (a) => {
    if (!window.confirm(`Cancel training assignment "${a.module_title}" for ${a.employee_name}?`)) return;
    try {
      const { data } = await api.delete(`/onboarding/assignments/${a.employee_id}/${a.module_id}`);
      toast.success(data.history_preserved
        ? `Deadline removed · ${a.employee_name}'s training history preserved`
        : `Assignment cancelled · ${a.module_title} removed from ${a.employee_name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) return <div className="p-6 text-center text-[var(--ink-fade)]">Loading assignments…</div>;

  const overdue = assignments.filter(a => a.overdue);
  const upcoming = assignments.filter(a => !a.overdue);

  return (
    <div className="space-y-4" data-testid="assignments-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>Training assignments</h2>
          <p className="text-sm text-[var(--ink-fade)]">Assign trainings to staff throughout the year with deadlines — annual re-certifications, new-policy sign-offs, refreshers.</p>
        </div>
        <button onClick={() => setDialogOpen(true)}
                className="px-3 py-1.5 rounded bg-[var(--accent)] text-white text-sm font-semibold hover:opacity-90"
                data-testid="new-assignment-btn">
          + New assignment
        </button>
      </div>

      {overdue.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3" data-testid="overdue-block">
          <div className="text-sm font-semibold text-red-800 mb-2">Overdue ({overdue.length})</div>
          <AssignmentTable rows={overdue} onEdit={setEditing} onDelete={deleteAssignment} />
        </div>
      )}

      <div className="rounded-lg border" data-testid="upcoming-block">
        <div className="px-3 py-2 text-sm font-semibold border-b bg-gray-50">Upcoming ({upcoming.length})</div>
        {upcoming.length === 0 ? (
          <div className="p-6 text-center text-sm text-[var(--ink-fade)]">No open assignments. Nice.</div>
        ) : <AssignmentTable rows={upcoming} onEdit={setEditing} onDelete={deleteAssignment} />}
      </div>

      {dialogOpen && (
        <AssignDialog api={api} modules={modules} staff={staff}
                      onClose={() => setDialogOpen(false)}
                      onSaved={() => { setDialogOpen(false); load(); }} />
      )}

      {editing && (
        <EditAssignmentDialog api={api} assignment={editing}
                              onClose={() => setEditing(null)}
                              onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function AssignmentTable({ rows, onEdit, onDelete }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wider text-[var(--ink-fade)] bg-gray-50 border-b">
          <th className="px-3 py-2">Employee</th>
          <th className="px-3 py-2">Training</th>
          <th className="px-3 py-2">Due</th>
          <th className="px-3 py-2">Status</th>
          <th className="px-3 py-2">Note</th>
          <th className="px-3 py-2 text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((a, i) => {
          const daysLabel = a.overdue
            ? `${Math.abs(a.days_until_due ?? 0)}d overdue`
            : (a.days_until_due != null ? `in ${a.days_until_due}d` : '');
          return (
            <tr key={i} className="border-b hover:bg-gray-50" data-testid={`assignment-row-${a.employee_id}-${a.module_id}`}>
              <td className="px-3 py-2">
                <div className="font-medium">{a.employee_name}</div>
                <div className="text-[10px] text-[var(--ink-fade)]">{a.employee_email}</div>
              </td>
              <td className="px-3 py-2">{a.module_title}</td>
              <td className={`px-3 py-2 ${a.overdue ? 'text-red-600 font-semibold' : ''}`}>
                {a.due_date} <span className="text-[10px] text-[var(--ink-fade)] ml-1">{daysLabel}</span>
              </td>
              <td className="px-3 py-2 text-xs capitalize">{a.status}</td>
              <td className="px-3 py-2 text-xs text-[var(--ink-fade)] max-w-[240px] truncate" title={a.assignment_note}>
                {a.assignment_note || '—'}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <button onClick={() => onEdit(a)} className="text-[var(--accent)] hover:underline text-xs mr-3" data-testid={`assign-edit-${a.employee_id}-${a.module_id}`}>Edit</button>
                <button onClick={() => onDelete(a)} className="text-red-600 hover:underline text-xs" data-testid={`assign-delete-${a.employee_id}-${a.module_id}`}>Delete</button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function EditAssignmentDialog({ api, assignment, onClose, onSaved }) {
  const [dueDate, setDueDate] = useState(assignment.due_date || '');
  const [note, setNote] = useState(assignment.assignment_note || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!dueDate) { toast.error('Due date required'); return; }
    setBusy(true);
    try {
      await api.patch(`/onboarding/assignments/${assignment.employee_id}/${assignment.module_id}`,
                      { due_date: dueDate, note });
      toast.success('Assignment updated');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="edit-assign-dialog">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="font-semibold">Edit assignment</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-sm text-[var(--ink-fade)]">
            <div><strong>{assignment.employee_name}</strong> · {assignment.module_title}</div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Due date *</label>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                   className="w-full border rounded px-2 py-1.5 text-sm"
                   data-testid="edit-assign-due" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Note</label>
            <input value={note} onChange={e => setNote(e.target.value)}
                   className="w-full border rounded px-2 py-1.5 text-sm"
                   data-testid="edit-assign-note" />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 bg-gray-50">
          <button onClick={onClose} className="px-3 py-1.5 rounded border text-sm">Cancel</button>
          <button onClick={save} disabled={busy}
                  className="px-3 py-1.5 rounded bg-[var(--accent)] text-white text-sm font-semibold disabled:opacity-50"
                  data-testid="edit-assign-save">
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignDialog({ api, modules, staff, onClose, onSaved }) {
  const [moduleId, setModuleId] = useState(modules[0]?.id || '');
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const [reset, setReset] = useState(false);
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const filtered = staff.filter(s => !filter || (s.name || '').toLowerCase().includes(filter.toLowerCase()));

  const save = async () => {
    if (!moduleId) { toast.error('Pick a training'); return; }
    if (!dueDate) { toast.error('Set a due date'); return; }
    if (selected.length === 0) { toast.error('Pick at least one staff member'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/onboarding/modules/${moduleId}/assign`, {
        employee_ids: selected, due_date: dueDate, note, reset_progress: reset,
      });
      toast.success(`Assigned "${data.module_title}" to ${data.assigned} staff · due ${data.due_date}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Assignment failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="assign-dialog">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="font-semibold">New training assignment</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold mb-1">Training</label>
            <select value={moduleId} onChange={e => setModuleId(e.target.value)}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                    data-testid="assign-module-select">
              {modules.map(m => <option key={m.id} value={m.id}>{`${m.title} (${m.hours}h)`}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">Due date *</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                     className="w-full border rounded px-2 py-1.5 text-sm"
                     data-testid="assign-due-date" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={reset} onChange={e => setReset(e.target.checked)}
                       data-testid="assign-reset" />
                Force retake (annual re-cert)
              </label>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Note <span className="text-gray-400 font-normal">(optional — shown to staff)</span></label>
            <input value={note} onChange={e => setNote(e.target.value)}
                   placeholder="e.g., Annual HIPAA refresh — please complete by end of quarter"
                   className="w-full border rounded px-2 py-1.5 text-sm"
                   data-testid="assign-note" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold">Assign to ({selected.length} selected)</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setSelected(filtered.map(s => String(s.id)))}
                        className="text-[10px] text-[var(--accent)] hover:underline"
                        data-testid="assign-select-all">Select all visible</button>
                <button type="button" onClick={() => setSelected([])}
                        className="text-[10px] text-gray-500 hover:underline">Clear</button>
              </div>
            </div>
            <input value={filter} onChange={e => setFilter(e.target.value)}
                   placeholder="Filter by name…"
                   className="w-full border rounded px-2 py-1.5 text-sm mb-2"
                   data-testid="assign-filter" />
            <div className="max-h-64 overflow-y-auto border rounded divide-y" data-testid="assign-staff-list">
              {filtered.map(s => {
                const id = String(s.id);
                const on = selected.includes(id);
                return (
                  <label key={id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer" data-testid={`assign-staff-${id}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(id)} />
                    <span className="text-sm flex-1">{s.name}</span>
                    <span className="text-[10px] text-[var(--ink-fade)]">{s.role}</span>
                  </label>
                );
              })}
              {filtered.length === 0 && <div className="text-xs text-[var(--ink-fade)] p-3 text-center">No staff match your filter.</div>}
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 bg-gray-50">
          <button onClick={onClose} className="px-3 py-1.5 rounded border text-sm">Cancel</button>
          <button onClick={save} disabled={busy}
                  className="px-3 py-1.5 rounded bg-[var(--accent)] text-white text-sm font-semibold disabled:opacity-50"
                  data-testid="assign-save-btn">
            {busy ? 'Assigning…' : 'Assign training'}
          </button>
        </div>
      </div>
    </div>
  );
}
